// ============================================
// SUBSCRIPTION / ENTITLEMENT TYPES (RevenueCat)
// ============================================

/**
 * RevenueCat entitlement lookup_keys.
 *
 * One product (2026-10-07): `spending_tools` ($15/mo, $100/yr) is the key
 * sold, and EVERY paid key grants everything — data connections, the
 * spending tools and the debt tools. `debt_tools` / `all_tools` are retired
 * lines (never sold, no holders) that stay honored; `data_sync` (the level
 * used in production) and `early_access` are legacy paid keys.
 *
 * Capability membership lives in constants/entitlements.ts. (Legacy Payoff
 * Genius 'pro' was fully retired 2026-07 and is not an entitlement.)
 */
export type EntitlementId =
  | 'spending_tools'
  | 'debt_tools'
  | 'all_tools'
  | 'data_sync'
  | 'early_access';

/**
 * Subscription status reflects the highest-priority active entitlement.
 */
export type SubscriptionStatus = 'free' | EntitlementId | 'unknown';

/** Product-line labels. Only `spending` is sold; `debt` and `all` are retired
 *  lines kept so a legacy grant still has a label and a price. */
export type ProductLine = 'spending' | 'debt' | 'all';

/** A purchasable membership product (web / Stripe). */
export interface MembershipProduct {
  line: ProductLine;
  label: string;
  amount: number;    // dollars
  priceId: string;   // Stripe price ID
}

export type SubscriptionStore = 'app_store' | 'play_store' | 'stripe' | 'promotional' | 'unknown';

/**
 * Normalize a raw `store` value into SubscriptionStore.
 *
 * The Firestore customer doc's `store` is written verbatim from RevenueCat
 * webhooks, which use UPPERCASE enum names ('APP_STORE', 'STRIPE',
 * 'PLAY_STORE', ...) — while this union (and every client comparison) is
 * lowercase. Casting without normalizing made `store === 'app_store'` false
 * for every native subscriber, which sent "Manage subscription" to the Stripe
 * portal instead of the phone's store settings (2026-07-17 TestFlight run).
 */
export function normalizeSubscriptionStore(raw: string | null | undefined): SubscriptionStore | undefined {
  if (!raw) return undefined;
  switch (raw.toLowerCase()) {
    case 'app_store':
    case 'mac_app_store':
      return 'app_store';
    case 'play_store':
      return 'play_store';
    case 'stripe':
      return 'stripe';
    case 'promotional':
      return 'promotional';
    default:
      return 'unknown';
  }
}

export interface CustomerSubscriptionInfo {
  status: SubscriptionStatus;
  /** Derived single source of truth written by post-consolidation backends */
  entitlementLevel?: string;
  activeEntitlementIds: EntitlementId[];
  hasActiveSubscription: boolean;
  activeProductId?: string;
  expirationDate?: string;
  willRenew?: boolean;
  monthlyAmount?: number;  // cents
  /** True while the active entitlement is a free trial (RC period_type TRIAL,
   *  written by the webhook). Drives trial-vs-paid conversion analytics. */
  isTrial?: boolean;
  /** Which store the subscription was purchased through */
  store?: SubscriptionStore;
  lastUpdated: string;
}

export interface PaywallOptions {
  onPurchaseComplete?: () => void;
  onPurchaseError?: (error: Error) => void;
  onDismiss?: () => void;
}

export interface PricePoint {
  amount: number;    // dollars (4-12)
  priceId: string;   // Stripe price ID
}
